import { motion } from 'framer-motion';
import { MapPin, Clock, Phone, Mail, Building2, Users, Award, Target, Eye, Heart, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const values = [
  {
    icon: Award,
    title: 'Excelencia',
    description: 'Nos comprometemos con los más altos estándares de calidad en cada programa de formación.',
  },
  {
    icon: Users,
    title: 'Compromiso',
    description: 'Acompañamos a cada estudiante en su camino hacia el éxito profesional.',
  },
  {
    icon: Heart,
    title: 'Vocación',
    description: 'Creemos en el poder transformador de la educación y la capacitación continua.',
  },
];

const stats = [
  { number: '+500', label: 'Egresados' },
  { number: '+5', label: 'Años de Experiencia' },
  { number: '98%', label: 'Satisfacción' },
  { number: '+10', label: 'Cursos Disponibles' },
];

export default function Nosotros() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1.5 bg-blue-500/20 text-blue-300 text-sm font-medium rounded-full mb-6">
              Conócenos
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading">
              Sobre{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Instituto Valparaíso
              </span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Más de 5 años formando profesionales de excelencia en la región de Valparaíso
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Image & Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-3xl -rotate-2" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/nosotro_naut.webp"
                  alt="Instituto Valparaíso - Nuestras Instalaciones"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6 font-heading">
                Nuestra Historia
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Instituto Valparaíso nació con la visión de transformar la educación profesional en la región. 
                Desde nuestros inicios, nos hemos dedicado a formar personas con las competencias necesarias 
                para destacar en el competitivo mundo laboral actual.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Como OTEC certificado bajo la norma NCH 2728, garantizamos programas de capacitación de alta 
                calidad, con metodologías innovadoras que combinan la teoría con la práctica. Nuestro equipo 
                de profesionales altamente calificados está comprometido con el éxito de cada estudiante.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Hoy, con más de 500 egresados y una tasa de satisfacción del 98%, seguimos trabajando para 
                ser el referente en formación profesional de la región de Valparaíso.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-blue-100 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-heading">
              Misión y Visión
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Los pilares que guían nuestro compromiso con la excelencia educativa
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-white shadow-xl border border-slate-100"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">Misión</h3>
              <p className="text-slate-600 leading-relaxed">
                Formar profesionales competentes y altamente capacitados, entregando educación de excelencia
                a través de metodologías innovadoras y una plataforma e-learning moderna. Nos comprometemos
                a brindar capacitación actualizada en seguridad privada, administración, contabilidad,
                automatización y gestión logística, permitiendo a nuestros alumnos y empresas alcanzar
                sus metas con certificaciones de reconocimiento nacional.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-white shadow-xl border border-slate-100"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">Visión</h3>
              <p className="text-slate-600 leading-relaxed">
                Ser el OTEC líder a nivel nacional en Chile, reconocido por nuestra excelencia académica,
                innovación tecnológica y compromiso con la satisfacción de nuestros alumnos. Aspiramos a
                expandir nuestra oferta formativa, formar alianzas estratégicas con empresas líderes y
                mantenernos siempre a la vanguardia, transformando vidas a través de una educación
                de calidad que impulse el desarrollo profesional y personal de cada estudiante.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-heading">
              Nuestros Valores
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Los principios que nos definen y guían cada día
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-100 text-center"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location & Contact Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1.5 bg-blue-500/20 text-blue-400 text-sm font-medium rounded-full mb-4">
              Encuéntranos
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
              Nuestra{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Ubicación
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Visítanos en nuestras instalaciones en el corazón de Valparaíso
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-2xl"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3344.526457687675!2d-71.62591972432621!3d-33.04260417355384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9689e1290677176b%3A0x45e517433e01ad2c!2sComunidad%20de%20Edificio%20Nautilus!5e0!3m2!1ses-419!2scl!4v1767842904612!5m2!1ses-419!2scl"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación Instituto Valparaíso"
                className="w-full"
              />
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Dirección</h3>
                    <p className="text-slate-400">
                      Edificio Nautilus<br />
                      Valparaíso, Región de Valparaíso<br />
                      Chile
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Horario de Atención</h3>
                    <p className="text-slate-400">
                      Lunes a Viernes<br />
                      <span className="text-cyan-400 font-semibold">10:00 AM - 14:00 PM</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Contacto</h3>
                    <p className="text-slate-400">
                      Escríbenos o llámanos para más información<br />
                      sobre nuestros cursos y programas.
                    </p>
                  </div>
                </div>
              </div>

              <motion.a
                href="/cursos"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
              >
                Inscríbete Ahora
                <ArrowRight className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
